import React, { useContext, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'

// Types
import { ROUTES_CONST } from '../../../../../const/routes-const'

// Libs
import { where } from 'firebase/firestore'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'

// Components
import InfiniteScrollDataTable from '../../../../../components/common/tables/InfiniteScrollDataTable'
import ViewsLoader from '../../../../../components/loader/ViewsLoader'
import { IconArrowRight } from '../../../../../components/icons/IconArrowRight'
import NoDataAvailable from './NoDataAvailable'
import customImageComponent from '../../../../../components/common/CustomImageComponent'
import { IconMessage2 } from '../../../../../components/icons/IconMessage2'

// Helpers
import { getAllRiders } from '../../../../../helpers/riders'
import { getConvertedData } from '../../../../../models/interface.helper'
import { getFloatPrice } from '../../../../../helpers/price'

// Services
import FirestoreService from '../../../../../services/firestoreService'
import { HandleModalContext } from '../../../../../layout/mainlayout/MainLayout'

// Store
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectedEvent, setRevenueCustomers } from '../../../../../store/events/eventsSlice'

// Hooks
import useGetUserEmailViaDocId from '../../../../../hooks/useGetUserEmailViaDocId'

// Models
import { RecipientModel } from '../../../../../models/recipients/recipients'

// Types
import { IRecipientInterface } from '../../../../../models/recipients/recipients.interface'
import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'

// Constants
import { CONST } from '../../../../../const/const'
import { MODAL_CONSTS } from '../../../../../const/modal-const'

interface IRecipientInterfaceEx extends IRecipientInterface {
  activeTab?: string
}

// Types
type IManageEventRegisterRevenueListProps = {
  eventId?: string
  activeTab: string
  className?: string
  searchValue: string
  isFullScreen?: boolean
}

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

const ACTIVE_TABS_KEYS = {
  ALL: 'all',
  PAID: 'paid',
  UNPAID: 'unpaid',
}

const LINKS = {
  MESSAGE_USER: '/messageuser/',
  REFUND_TO_EXHIBITOR: ROUTES_CONST.EXHIBITOR_REFUND.URL,
}

const COLS = {
  EXHIBITOR: 'Exhibitor',
  PAYMENT_STATUS: 'Status',
  AMOUNT: 'Amount',
  REGISTERED: 'Registered',
  RECEIPT: 'Receipt',
  REFUNDED: 'Action',
  SCRATCHED: 'Scratched',
}

interface IGroupedRegistrationByDay {
  [key: string]: IRegistrationByDayInterface[]
}

const ManageEventRegisterRevenueList = (props: IManageEventRegisterRevenueListProps) => {
  const dispatch = useAppDispatch()
  const { getEmail } = useGetUserEmailViaDocId()
  const handleModalContext = useContext(HandleModalContext)
  const { eventId } = useParams<{ eventId?: any }>()

  const rowIndexToLoadRef = useRef(-1)
  const setRowIndexToLoad = useState(-1)[1]

  const currentEvent = useAppSelector(selectedEvent)

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<IRecipientInterface[]>([])
  const [allData, setAllData] = useState<IRecipientInterfaceEx[]>([])
  const [groupedRegisteredUsersByDay, setGroupedRegisteredUsersByDay] =
    useState<IGroupedRegistrationByDay>({})

  useEffect(() => {
    if (eventId) getUsersList().then()
  }, [eventId])

  useEffect(() => {
    let allData_ = cloneDeep(allData)
    let filteredData: IRecipientInterface[] = []

    if (props.searchValue) {
      allData_ = allData.filter((itm) =>
        itm.recipientName?.toLowerCase().includes(props.searchValue.toLowerCase())
      )
    }

    switch (props.activeTab?.toLocaleLowerCase()) {
      case ACTIVE_TABS_KEYS.PAID:
        filteredData = allData_.filter((itm) => itm.amountPaid > 0)
        break

      case ACTIVE_TABS_KEYS.UNPAID:
        filteredData = allData_.filter((itm) => itm.amountPaid < itm.amountTotal)
        break

      case ACTIVE_TABS_KEYS.ALL:
        filteredData = allData_
        break
    }

    setData(
      filteredData.map((item) => ({ ...item, activeTab: props.activeTab?.toLocaleLowerCase() }))
    )
  }, [props.activeTab, allData, props.searchValue])

  const sendEmail = async (index: number, userId: string | null) => {
    setRowIndexToLoad(index)
    rowIndexToLoadRef.current = index

    let emailId = await getEmail({ userId })
    if (emailId) {
      handleModalContext?.handleModal(true, MODAL_CONSTS.SEND_MESSAGE, { emailId })
    }

    setRowIndexToLoad(-1)
    rowIndexToLoadRef.current = -1
  }

  /**
   * @info Returns structured users registration details as per datatable
   */
  const getUsersList = async () => {
    setLoading(true)
    try {
      const registeredUsersByDay = await getAllRiders([eventId])
      const groupedData = registeredUsersByDay.reduce((acc: any, item) => {
        if (!acc[item.userId!]) {
          acc[item.userId!] = []
        }
        acc[item.userId!].push(item)
        return acc
      }, {})

      setGroupedRegisteredUsersByDay(groupedData)
      let recipient: IRecipientInterface | null = null
      let newData: IRecipientInterface[] = []

      const recipientSnapshots = await FirestoreService.filterItems(COLLECTIONS.RECIPIENT.NAME, [
        where(COLLECTIONS.RECIPIENT.FIELDS.EVENT_ID.KEY, '==', eventId),
      ])

      recipientSnapshots.forEach((currDoc) => {
        recipient = getConvertedData(RecipientModel.fromFirestoreDoc(currDoc).toObject())
        if (recipient) newData.push(recipient)
      })

      if (currentEvent && (!currentEvent.revenueCustomers || !currentEvent.revenueCustomers[0])) {
        dispatch(setRevenueCustomers(newData))
      }

      setAllData(newData)
    } catch (error) {
      console.error(error, 'error')
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      name: (
        <span className="text-SeabiscuitDark200ThemeColor font-semibold text-sm flex items-center gap-2 ">
          <span className="whitespace-nowrap">Exhibitor</span>
        </span>
      ),
      cell: (row: IRecipientInterfaceEx) => {
        const countEntries = groupedRegisteredUsersByDay[row.userId!]?.length || 0
        const countScratchedEntries =
          groupedRegisteredUsersByDay[row.userId!]?.filter((reg) => reg.isScratched).length || 0

        return (
          <div className="flex items-center text-SeabiscuitDark200ThemeColor font-semibold capitalize">
            {!!row?.id && !!eventId && !!row?.id ? (
              <div className="flex items-center">
                {customImageComponent(
                  row.recipientProfilePicture,
                  row.recipientName,
                  '!w-[45px] !h-[45px] object-cover rounded-full mr-5 shrink-0'
                )}
                <div>
                  <p>{row?.recipientName}</p>
                  <p className={'text-SeabiscuitDark200ThemeColor/50 font-normal mt-0.5 lowercase'}>
                    {countEntries} entries • {countScratchedEntries} items scratched
                  </p>
                </div>
              </div>
            ) : (
              <>
                {customImageComponent(
                  row.recipientProfilePicture,
                  row.recipientName,
                  'w-[45px] h-[45px] rounded-full mr-5 shrink-0'
                )}
                {row?.recipientName}
              </>
            )}
          </div>
        )
      },
      width: '65%',
    },
    {
      name: (
        <span className="w-full text-right text-SeabiscuitDark200ThemeColor font-semibold text-sm flex items-center justify-end px-4 gap-2">
          {COLS.RECEIPT}
        </span>
      ),
      cell: (row: IRecipientInterfaceEx) => (
        <div className={'w-full py-4 text-right'}>
          {row.activeTab === ACTIVE_TABS_KEYS.PAID && (
            <p>{getFloatPrice(`${row.amountPaid}`)} paid</p>
          )}
          {row.activeTab === ACTIVE_TABS_KEYS.UNPAID && (
            <p>{getFloatPrice(String(row.amountTotal - row.amountPaid))} owing</p>
          )}

          {row.activeTab === ACTIVE_TABS_KEYS.ALL && (
            <p>
              {row.amountTotal - row.amountPaid > 0 &&
                `${getFloatPrice(String(row.amountTotal - row.amountPaid))} owing • `}
              {row.amountPaid > 0 && `${getFloatPrice(`${row.amountPaid}`)} paid`}
            </p>
          )}
          <p className={'text-SeabiscuitDark200ThemeColor/50'}>
            {getFloatPrice(String(row.amountScratched))} scratched •{' '}
            {getFloatPrice(String(row.amountRefunded))} refunded
          </p>
        </div>
      ),
    },
    {
      name: (
        <span className="text-SeabiscuitDark200ThemeColor font-semibold text-sm flex justify-center pl-10 gap-2 items-center">
          <span className="whitespace-nowrap">Refund</span>
        </span>
      ),
      cell: (row: IRecipientInterfaceEx, rowIndex: number) => (
        <>
          <div className={'flex gap-2 '}>
            <button
              className={
                'w-[45px] h-[45px] rounded-lg border-SeabiscuitLightThemeColorD3 border border-solid flex items-center justify-center transition-all hover:bg-SeabiscuitGrayThemeColor'
              }
              onClick={async () => await sendEmail(rowIndex, row.recipientId)}
            >
              <IconMessage2 />
            </button>

            <Link
              to={`${LINKS.REFUND_TO_EXHIBITOR}/${row?.recipientId}/${eventId}/${row?.registrationDocId}`}
              className={
                'w-[45px] h-[45px] rounded-lg  border-SeabiscuitGrayThemeColor bg-SeabiscuitGrayThemeColor border border-solid flex items-center justify-center transition-all hover:bg-SeabiscuitLightThemeColorD3'
              }
            >
              <IconArrowRight />
            </Link>
          </div>
        </>
      ),
      width: '140px',
    },
  ]

  return (
    <InfiniteScrollDataTable
      data={data}
      hasMore={false}
      columns={columns}
      fixedHeader={true}
      progressPending={loading}
      reloadOnBasedOf={props.activeTab}
      className={clsx('!rounded-none', data.length || loading ? 'h-[50vh]' : '')}
      noDataComponent={
        <NoDataAvailable
          buttonText={'Edit registrations >'}
          imgSrc="/assets/og_icons/People-4.svg"
          text="No exhibitors have registered for this event"
        />
      }
      progressComponent={
        <ViewsLoader
          className="flex items-center w-full justify-center my-10"
          size="md"
          color="#F70763"
        />
      }
    />
  )
}

export default ManageEventRegisterRevenueList
